import { Badge } from "@car/ui/components/badge";
import { Button } from "@car/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@car/ui/components/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@car/ui/components/dialog";
import { Progress } from "@car/ui/components/progress";
import { Skeleton } from "@car/ui/components/skeleton";
import { Spinner } from "@car/ui/components/spinner";
import { cn, SEP } from "@car/ui/lib/utils";
import {
  CheckmarkCircle02Icon,
  GiftIcon,
  Medal01Icon,
  Wallet01Icon,
} from "@hugeicons-pro/core-stroke-rounded";
import { HugeiconsIcon } from "@hugeicons/react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { PageHeader, PanelQueryError } from "@/components/panel-bits";
import { faDate, faNumber, faPercent, faToman } from "@/lib/format";
import { SURVEY_WINDOW_DAYS, tierKeys, type Reward } from "@/lib/panel";
import { orpc } from "@/utils/orpc";

export const Route = createFileRoute("/panel/club")({
  head: () => ({ meta: [{ title: "باشگاه مشتریان | پنل مشتریان تابان خودرو" }] }),
  component: ClubPage,
});

const tierRank = (key: string) => tierKeys.findIndex((candidate) => candidate === key);

function ClubPage() {
  const summaryQuery = useQuery(orpc.panel.club.summary.queryOptions());
  const historyQuery = useQuery(orpc.panel.club.history.queryOptions());

  if (summaryQuery.isPending) {
    return (
      <div className="grid gap-4">
        <Skeleton className="h-9 w-64" />
        <Skeleton className="h-40 rounded-xl" />
        <Skeleton className="h-56 rounded-xl" />
      </div>
    );
  }

  if (summaryQuery.isError) {
    return <PanelQueryError title="باشگاه مشتریان بارگیری نشد" query={summaryQuery} />;
  }

  const { club, tiers, rewards, claims, wallet, settings } = summaryQuery.data;
  const earnWays = [
    {
      title: "پرداخت فاکتور خدمات",
      text: `هر ${faToman(settings.serviceTomanPerPoint)} یک امتیاز (به‌جز سهم پرداخت‌شده از کیف پول)`,
    },
    {
      title: "خرید خودرو",
      text: `هنگام تحویل، هر ${faToman(settings.purchaseTomanPerPoint)} یک امتیاز`,
    },
    {
      title: "نظرسنجی پس از سرویس",
      text:
        settings.surveyPoints > 0
          ? `${faNumber(settings.surveyPoints)} امتیاز برای نظرسنجی هر سرویس نمایندگی، تا ${faNumber(SURVEY_WINDOW_DAYS)} روز پس از تحویل`
          : "فعلاً امتیازی ندارد",
    },
  ];

  return (
    <div className="grid gap-6">
      <PageHeader
        title="باشگاه مشتریان"
        description="امتیاز جمع کنید، سطح بگیرید و از جوایز و تخفیف‌های اختصاصی استفاده کنید."
      />

      <Card>
        <CardContent className="grid gap-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className="grid size-12 place-items-center rounded-xl bg-primary/10 text-primary">
                <HugeiconsIcon icon={Medal01Icon} strokeWidth={1.8} className="size-6" />
              </span>
              <div className="grid">
                <span className="font-black">سطح {club.tier.label}</span>
                <span className="text-sm text-muted-foreground">
                  مجموع امتیاز طول عمر: {faNumber(club.lifetime)}
                </span>
              </div>
            </div>
            <div className="grid text-start sm:text-end">
              <span className="text-2xl font-black">{faNumber(club.balance)}</span>
              <span className="text-sm text-muted-foreground">امتیاز قابل استفاده</span>
            </div>
          </div>
          {club.nextTier ? (
            <div className="grid gap-1.5">
              <Progress
                value={Math.round(club.progressToNext * 100)}
                aria-label="پیشرفت تا سطح بعدی"
              />
              <span className="text-sm text-muted-foreground">
                {faNumber(club.remainingToNext)} امتیاز دیگر تا سطح {club.nextTier.label}
              </span>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">شما در بالاترین سطح باشگاه هستید.</p>
          )}
        </CardContent>
      </Card>

      <section aria-label="کیف پول" className="grid gap-3">
        <h2 className="font-bold">کیف پول</h2>
        <Card>
          <CardContent className="grid gap-4">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="grid size-12 place-items-center rounded-xl bg-primary/10 text-primary">
                  <HugeiconsIcon icon={Wallet01Icon} strokeWidth={1.8} className="size-6" />
                </span>
                <div className="grid">
                  <span className="text-2xl font-black">{faToman(wallet.balance)}</span>
                  <span className="text-sm text-muted-foreground">موجودی قابل استفاده</span>
                </div>
              </div>
              <p className="max-w-sm text-sm text-muted-foreground">
                {club.tier.cashbackBp > 0
                  ? `در سطح ${club.tier.label}، ${faPercent(club.tier.cashbackBp / 100)} از هر پرداخت به کیف پولتان برمی‌گردد.`
                  : "در سطح‌هایی که کش‌بک دارند، بخشی از هر پرداخت به کیف پولتان برمی‌گردد."}{" "}
                {settings.walletMaxPercent > 0
                  ? `موجودی تا ${faPercent(settings.walletMaxPercent)} هر پرداخت را می‌پوشاند؛ سهمی که از کیف پول پرداخت می‌شود امتیاز و کش‌بک ندارد.`
                  : ""}
              </p>
            </div>
            {wallet.entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                هنوز گردشی در کیف پول شما ثبت نشده است.
              </p>
            ) : (
              <ol className="grid divide-y border-t">
                {wallet.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 py-3 last:pb-0 text-sm"
                  >
                    <div className="grid">
                      <span className="font-bold">{entry.title}</span>
                      <span className="text-muted-foreground">
                        {entry.kindLabel}
                        {SEP}
                        {faDate(entry.dateIso)}
                      </span>
                    </div>
                    <span
                      className={cn(
                        "shrink-0 font-black",
                        entry.amount > 0
                          ? "text-emerald-700 dark:text-emerald-400"
                          : "text-foreground",
                      )}
                    >
                      {entry.amount > 0 ? "+" : "−"}
                      {faToman(Math.abs(entry.amount))}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </section>

      <section aria-label="راه‌های کسب امتیاز">
        <h2 className="mb-3 font-bold">راه‌های کسب امتیاز</h2>
        <div className="grid gap-3 sm:grid-cols-3">
          {earnWays.map((way) => (
            <Card key={way.title} size="sm">
              <CardContent className="grid gap-1">
                <span className="font-bold">{way.title}</span>
                <span className="text-sm text-muted-foreground">{way.text}</span>
              </CardContent>
            </Card>
          ))}
        </div>
        <p className="mt-2 text-sm text-muted-foreground">
          سطح شما با مجموع امتیازهایی که تا امروز گرفته‌اید تعیین می‌شود و با دریافت جایزه پایین
          نمی‌آید.
        </p>
      </section>

      <section aria-label="سطوح باشگاه">
        <h2 className="mb-3 font-bold">سطوح و مزایا</h2>
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {tiers.map((tier) => {
            // a tier granted by staff counts as reached even below its threshold
            const reached =
              club.lifetime >= tier.threshold || tierRank(tier.key) < tierRank(club.tier.key);
            const current = club.tier.key === tier.key;
            return (
              <Card
                key={tier.key}
                className={cn(current && "border-primary ring-2 ring-primary/30")}
              >
                <CardHeader className="pb-2">
                  <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base">{tier.label}</CardTitle>
                    {current ? (
                      <Badge>سطح فعلی شما</Badge>
                    ) : reached ? (
                      <Badge variant="secondary">گذرانده‌اید</Badge>
                    ) : null}
                  </div>
                  <p className="text-sm text-muted-foreground">
                    {tier.threshold === 0
                      ? "از روز عضویت"
                      : `از ${faNumber(tier.threshold)} امتیاز`}
                  </p>
                </CardHeader>
                <CardContent className="grid gap-2">
                  {tier.cashbackBp > 0 ? (
                    <Badge variant="outline" className="w-fit">
                      <HugeiconsIcon icon={Wallet01Icon} strokeWidth={1.8} />
                      {faPercent(tier.cashbackBp / 100)} کش‌بک در کیف پول
                    </Badge>
                  ) : null}
                  <ul className="grid gap-1.5 text-sm">
                    {tier.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-1.5">
                        <HugeiconsIcon
                          icon={CheckmarkCircle02Icon}
                          strokeWidth={1.8}
                          className="mt-0.5 size-4 shrink-0 text-primary"
                        />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </section>

      <section aria-label="جوایز">
        <h2 className="mb-3 font-bold">جوایز و تخفیف‌ها</h2>
        {rewards.length === 0 ? (
          <p className="text-sm text-muted-foreground">فعلاً جایزه‌ای برای دریافت تعریف نشده است.</p>
        ) : null}
        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {rewards.map((reward) => {
            const tierLocked = tierRank(club.tier.key) < tierRank(reward.minTier);
            const affordable = club.balance >= reward.cost;
            return (
              <RewardCard
                key={reward.id}
                reward={reward}
                tierLocked={tierLocked}
                affordable={affordable}
                tierLabel={tiers.find((tier) => tier.key === reward.minTier)?.label ?? ""}
              />
            );
          })}
        </div>
      </section>

      {claims.length > 0 ? (
        <section aria-label="جوایز دریافتی">
          <h2 className="mb-3 font-bold">جوایز دریافتی شما</h2>
          <Card>
            <CardContent>
              <ol className="grid divide-y">
                {claims.map((claim) => (
                  <li
                    key={claim.id}
                    className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 text-sm"
                  >
                    <div className="grid">
                      <span className="font-bold">{claim.title}</span>
                      <span className="text-muted-foreground">
                        {faDate(claim.createdIso)}
                        {SEP}
                        {faNumber(claim.cost)} امتیاز
                        {claim.status === "used" && claim.closedIso
                          ? `${SEP}استفاده در ${faDate(claim.closedIso)}${claim.branchName ? `، ${claim.branchName}` : ""}`
                          : ""}
                        {claim.status === "cancelled" ? `${SEP}امتیاز به موجودی برگشت` : ""}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span
                        dir="ltr"
                        className={cn(
                          "rounded-md border px-2 py-0.5 font-mono font-bold tracking-wider",
                          claim.status !== "issued" && "text-muted-foreground line-through",
                        )}
                      >
                        {claim.code}
                      </span>
                      <Badge variant={claim.status === "issued" ? "default" : "secondary"}>
                        {claim.statusLabel}
                      </Badge>
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </section>
      ) : null}

      <section aria-label="تاریخچه امتیازات">
        <h2 className="mb-3 font-bold">تاریخچه امتیازات</h2>
        <Card>
          <CardContent>
            {historyQuery.isPending ? (
              <div className="grid gap-2">
                {[0, 1, 2].map((index) => (
                  <Skeleton key={index} className="h-8 rounded-md" />
                ))}
              </div>
            ) : historyQuery.isError ? (
              <PanelQueryError title="تاریخچه امتیازات بارگیری نشد" query={historyQuery} />
            ) : historyQuery.data.entries.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                هنوز امتیازی ثبت نشده است؛ با پرداخت فاکتور و نظرسنجی امتیاز بگیرید.
              </p>
            ) : (
              <ol className="grid divide-y">
                {historyQuery.data.entries.map((entry) => (
                  <li
                    key={entry.id}
                    className="flex items-center justify-between gap-3 py-3 first:pt-0 last:pb-0 text-sm"
                  >
                    <div className="grid">
                      <span className="font-bold">{entry.title}</span>
                      <span className="text-muted-foreground">{faDate(entry.dateIso)}</span>
                    </div>
                    <span
                      className={cn(
                        "font-black",
                        entry.points > 0
                          ? "text-emerald-700 dark:text-emerald-400"
                          : "text-foreground",
                      )}
                    >
                      {entry.points > 0
                        ? `${faNumber(entry.points)}+`
                        : entry.points < 0
                          ? `${faNumber(-entry.points)}−`
                          : faNumber(0)}
                    </span>
                  </li>
                ))}
              </ol>
            )}
          </CardContent>
        </Card>
      </section>
    </div>
  );
}

function RewardCard({
  reward,
  tierLocked,
  affordable,
  tierLabel,
}: {
  reward: Reward;
  tierLocked: boolean;
  affordable: boolean;
  tierLabel: string;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const mutation = useMutation(
    orpc.panel.club.redeem.mutationOptions({
      onSuccess: (result) => {
        if (result.ok) {
          toast.success(`جایزه «${reward.title}» ثبت شد؛ کد شما: ${result.code}`);
          queryClient.invalidateQueries();
          setOpen(false);
        } else {
          toast.error(result.reason);
        }
      },
    }),
  );

  return (
    <Card className={cn(tierLocked && "opacity-75")}>
      <CardHeader className="pb-2">
        <div className="flex items-start justify-between gap-2">
          <CardTitle className="text-base">{reward.title}</CardTitle>
          {tierLocked ? <Badge variant="outline">ویژه سطح {tierLabel}+</Badge> : null}
        </div>
        <p className="text-sm text-muted-foreground">{reward.description}</p>
      </CardHeader>
      <CardContent className="mt-auto flex items-center justify-between gap-2">
        <span className="font-black">{faNumber(reward.cost)} امتیاز</span>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger
            render={<Button size="sm" variant="outline" disabled={tierLocked || !affordable} />}
          >
            <HugeiconsIcon icon={GiftIcon} strokeWidth={1.8} />
            {tierLocked ? "قفل سطح" : affordable ? "دریافت جایزه" : "امتیاز ناکافی"}
          </DialogTrigger>
          <DialogContent lang="fa" dir="rtl">
            <DialogHeader>
              <DialogTitle>دریافت «{reward.title}»</DialogTitle>
              <DialogDescription>
                {faNumber(reward.cost)} امتیاز از موجودی شما کم می‌شود و یک کد جایزه برایتان صادر
                می‌شود. هنگام مراجعه به شعبه، کد را به پذیرش بگویید؛ اگر جایزه لغو شود امتیاز به
                موجودی شما برمی‌گردد.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                disabled={mutation.isPending}
                onClick={() => mutation.mutate({ rewardId: reward.id })}
              >
                {mutation.isPending ? <Spinner className="size-4" /> : null}
                تأیید و دریافت
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardContent>
    </Card>
  );
}
